import {
	type ClassicyStoreSystemAppearanceManager,
	getAllThemes,
	getTheme,
} from "@/SystemFolder/ControlPanels/AppearanceManager/ClassicyAppearance";
import { ClassicyIcons } from "@/SystemFolder/ControlPanels/AppearanceManager/ClassicyIcons";
import {
	hasApp,
	hasAppAndFileType,
	hasAppAndFileTypes,
	hasAppAndPath,
	hasDesktopAppRef,
} from "@/SystemFolder/ControlPanels/AppManager/ClassicyActionPredicates";
import {
	type ClassicyActionTrust,
	isActionTrustPermitted,
} from "@/SystemFolder/ControlPanels/AppManager/ClassicyActionTrust";
import {
	activateApp,
	closeApp,
	deFocusApps,
	focusApp,
	focusWindow,
	getDefaultAppForFileType,
	loadApp,
	openApp,
	pickSuccessorApp,
} from "@/SystemFolder/ControlPanels/AppManager/ClassicyAppHelpers";
import { classicyDateTimeManagerEventHandler } from "@/SystemFolder/ControlPanels/DateAndTimeManager/ClassicyDateAndTimeEventHandler";
import { DEFAULT_ALERT_SOUND } from "@/SystemFolder/ControlPanels/SoundManager/ClassicyAlertSounds";
import type { ClassicyStoreSystemSoundManager } from "@/SystemFolder/ControlPanels/SoundManager/ClassicySoundManagerContext";
import {
	type ClassicyStoreSystemBootManager,
	classicyBootEventHandler,
} from "@/SystemFolder/SystemResources/Boot/ClassicyBootManager";
import { classicyDesktopIconEventHandler } from "@/SystemFolder/SystemResources/Desktop/ClassicyDesktopIconContext";
import {
	type ClassicyStoreSystemDesktopManager,
	classicyDesktopEventHandler,
} from "@/SystemFolder/SystemResources/Desktop/ClassicyDesktopManager";
import { classicyWindowEventHandler } from "@/SystemFolder/SystemResources/Desktop/ClassicyDesktopWindowManagerContext";
import { ClassicyFileSystemEntryFileType } from "@/SystemFolder/SystemResources/File/ClassicyFileSystemModel";
import { classicyLog } from "@/SystemFolder/SystemResources/Log/ClassicyLog";
import type { ClassicyMenuItem } from "@/SystemFolder/SystemResources/Menu/ClassicyMenu";
import type { DeepPartial } from "@/SystemFolder/SystemResources/Utils/deepMerge";
import { deepMergeReplacingArrays } from "@/SystemFolder/SystemResources/Utils/deepMerge";

const macosIcon = ClassicyIcons.system.macos;

type JsonPrimitive = string | number | boolean | null;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = Array<JsonValue>;
type JsonValue = JsonPrimitive | JsonObject | JsonArray;

export interface ClassicyStoreSystemAppManager
	extends ClassicyStoreSystemManager {
	apps: Record<string, ClassicyStoreSystemApp>;
	fileTypeHandlers: Record<ClassicyFileSystemEntryFileType, string>;
	focusedAppId?: string;
}

export interface ClassicyStoreSystemApp {
	id: string;
	name: string;
	icon: string;
	windows: ClassicyStoreSystemAppWindow[];
	open: boolean;
	data?: Record<string, unknown>;
	focused?: boolean;
	// Wall-clock (Date.now) of the app's most recent focus. The only cross-app
	// recency signal in the store — window zOrder is per-app. Used to pick the
	// successor that inherits the menu bar when the front app quits.
	lastFocusedAt?: number;
	lastAccessedWindowId?: string;
	noDesktopIcon?: boolean;
	extension?: boolean;
	debug?: boolean;
	options?: Record<string, unknown>[];
	appMenu?: ClassicyMenuItem[];
	contextMenu?: ClassicyMenuItem[];
	handlesFileTypes?: ClassicyFileSystemEntryFileType[];
}

export interface ClassicyStoreSystemAppWindow {
	closed: boolean;
	id: string;
	appId?: string;
	title?: string;
	icon?: string;
	size: [number, number];
	position: [number, number];
	minimumSize: [number, number];
	focused?: boolean;
	default?: boolean;
	resizing?: boolean;
	zoomed?: boolean;
	collapsed?: boolean;
	dragging?: boolean;
	moving?: boolean;
	modal?: boolean;
	menuBar?: ClassicyMenuItem[];
	zOrder?: number;
	// Platinum window class (#205). Utility (tool-palette) windows are excluded
	// from automatic focus succession; absent ⇒ treated as "document".
	windowType?: "document" | "utility";
	options?: Record<string, JsonValue>[];
}

export interface ClassicyStore {
	System: ClassicyStoreSystem;
}

export type { DeepPartial } from "@/SystemFolder/SystemResources/Utils/deepMerge";

export interface ClassicyStoreSystem {
	Manager: {
		Desktop: ClassicyStoreSystemDesktopManager;
		Sound: ClassicyStoreSystemSoundManager;
		Applications: ClassicyStoreSystemAppManager;
		Appearance: ClassicyStoreSystemAppearanceManager;
		DateAndTime: ClassicyStoreSystemDateAndTimeManager;
		Boot: ClassicyStoreSystemBootManager;
		Keyboard: ClassicyStoreSystemKeyboardManager;
	};
}

export interface ClassicyStoreSystemKeyboardManager
	extends ClassicyStoreSystemManager {
	/** Canonical chords each app claims (auto-derived from its menu). Keyed by appId. */
	app: Record<string, string[]>;
	/** Always-active chords owned by the desktop's Apple/system/Help menus. */
	system: string[];
	/** Extension globals: canonical chord → dispatched action. Unique; first-wins. */
	global: Record<
		string,
		{ appId: string; event: string; eventData?: Record<string, unknown> }
	>;
}

export interface ClassicyStoreSystemDateAndTimeManager
	extends ClassicyStoreSystemManager {
	dateTime: string;
	timeZoneOffset: string;
	militaryTime: boolean;
	displaySeconds: boolean;
	displayPeriod: boolean;
	displayDay: boolean;
	displayLongDay: boolean;
	flashSeparators: boolean;
	show: boolean;
	paused: boolean;
	minDateTime: string | null;
	maxDateTime: string | null;
	boundaryLocked: boolean;
	/** When true, the Date & Time Manager's date/time editors are disabled
	 *  (timezone stays editable). Set by the host app, e.g. while a server
	 *  forces the clock. Not a write guard — DateTimeSet still applies. */
	dateTimeLocked: boolean;
	/** When true, ClassicyManagerDateTimeSync replaces only the time-of-day and
	 *  preserves the displayed calendar date. Set by the host app, e.g. for a
	 *  desktop pinned to a fictional date that still wants a real clock. */
	syncTimeOnly: boolean;
}

// biome-ignore lint/complexity/noBannedTypes: Intentional empty base type extended by manager interfaces
export type ClassicyStoreSystemManager = {};

export {
	activateApp,
	closeApp,
	deFocusApps,
	focusApp,
	focusWindow,
	getDefaultAppForFileType,
	loadApp,
	openApp,
};

export type ActionMessage = Record<string, unknown> & {
	type: string;
};

export type AppEventHandler = (
	ds: ClassicyStore,
	action: ActionMessage,
) => ClassicyStore;

const pluginEventHandlers: Array<{
	prefix: string;
	handler: AppEventHandler;
}> = [];

/**
 * @deprecated Prefer `registerApp` (ClassicyAppManifest.ts), which registers
 * the handler AND the app's manifest (action/state schemas, commentary,
 * scriptable exposure) in one call. This remains the underlying primitive
 * `registerApp` delegates to; direct use keeps working but records no
 * manifest, so balloon help, HyperCard discovery, and dev-mode state
 * validation see nothing for the app.
 *
 * Register an event handler for a given action type prefix.
 * Call this at module load time from your app's context file.
 * Handlers are checked before the generic ClassicyApp* handler.
 * Registering the same prefix a second time is a no-op.
 */
export function registerAppEventHandler(
	prefix: string,
	handler: AppEventHandler,
): void {
	if (pluginEventHandlers.some((entry) => entry.prefix === prefix)) {
		return;
	}
	pluginEventHandlers.push({ prefix, handler });
}

/**
 * Dispatch an action to the registered handler for the given prefix.
 * Used for cross-app orchestration without a direct import between apps.
 */
export function dispatchToPlugin(
	ds: ClassicyStore,
	prefix: string,
	action: ActionMessage,
): ClassicyStore {
	const plugin = pluginEventHandlers.find((entry) => entry.prefix === prefix);
	if (plugin) {
		return plugin.handler(ds, action);
	}
	// Without this the caller's action vanishes silently — the symptom is "the
	// feature did nothing at all", with nothing to grep for. Almost always means
	// the owning app's context module was never pulled into the module graph.
	classicyLog("warn", "dispatchToPlugin", "No handler registered for prefix", {
		prefix,
		type: action.type,
	});
	return ds;
}

export const classicyAppEventHandler = (
	ds: ClassicyStore,
	action: ActionMessage,
) => {
	switch (action.type) {
		case "ClassicyAppOpen": {
			if (hasDesktopAppRef(action)) {
				openApp(ds, action.app.id, action.app.name, action.app.icon);
			}
			break;
		}
		case "ClassicyAppLoad": {
			if (hasDesktopAppRef(action)) {
				loadApp(
					ds,
					action.app.id,
					action.app.name,
					action.app.icon,
					Array.isArray(action.contextMenu)
						? (action.contextMenu as ClassicyMenuItem[])
						: undefined,
					action.extension === true,
				);
			}
			break;
		}
		case "ClassicyAppClose": {
			const closingId = hasApp(action) ? action.app.id : undefined;
			if (closingId) {
				closeApp(ds, closingId);
			}

			// Hand the menu bar to the most recently focused OTHER app, so quitting
			// the front app immediately transfers control instead of leaving the
			// dead app's menu on screen. Finder is the floor: when no other real app
			// remains, fall back to it (the desktop menu bar with "Special"). If even
			// Finder is closed, no app owns the bar.
			const successorId = pickSuccessorApp(ds, closingId ?? "");
			const finderOpen =
				ds.System.Manager.Applications.apps["Finder.app"]?.open === true;

			if (successorId) {
				focusApp(ds, successorId);
			} else if (finderOpen) {
				focusApp(ds, "Finder.app");
			} else {
				deFocusApps(ds);
			}

			break;
		}
		case "ClassicyAppFocus": {
			if (hasApp(action)) {
				focusApp(ds, action.app.id);
			}
			break;
		}
		case "ClassicyAppActivate": {
			if (hasApp(action)) {
				activateApp(ds, action.app.id);
			}
			break;
		}
		case "ClassicyAppRegisterFileTypes": {
			if (hasAppAndFileTypes(action)) {
				const app = ds.System.Manager.Applications.apps[action.app.id];
				if (app) {
					const existing = app.handlesFileTypes ?? [];
					app.handlesFileTypes = Array.from(
						new Set([
							...existing,
							...(action.fileTypes as ClassicyFileSystemEntryFileType[]),
						]),
					);
				}
				for (const ft of action.fileTypes) {
					const current =
						ds.System.Manager.Applications.fileTypeHandlers[
							ft as ClassicyFileSystemEntryFileType
						];
					if (!current || current === "Finder.app") {
						ds.System.Manager.Applications.fileTypeHandlers[
							ft as ClassicyFileSystemEntryFileType
						] = action.app.id;
					}
				}
			}
			break;
		}
		case "ClassicyAppUnregisterFileTypes": {
			if (hasAppAndFileTypes(action)) {
				const app = ds.System.Manager.Applications.apps[action.app.id];
				if (app) {
					app.handlesFileTypes = (app.handlesFileTypes ?? []).filter(
						(t: ClassicyFileSystemEntryFileType) =>
							!(action.fileTypes as unknown[]).includes(t),
					);
					for (const ft of action.fileTypes) {
						const key = ft as ClassicyFileSystemEntryFileType;
						if (
							ds.System.Manager.Applications.fileTypeHandlers[key] ===
							action.app.id
						) {
							ds.System.Manager.Applications.fileTypeHandlers[key] =
								"Finder.app";
						}
					}
				}
			}
			break;
		}
		case "ClassicyAppSetDefaultFileTypeHandler": {
			if (hasAppAndFileType(action)) {
				ds.System.Manager.Applications.fileTypeHandlers[
					action.fileType as ClassicyFileSystemEntryFileType
				] = action.app.id;
			}
			break;
		}
		default: {
			if (action.type.endsWith("OpenFile") && hasAppAndPath(action)) {
				const app = ds.System.Manager.Applications.apps[action.app.id];
				if (app) {
					if (!app.data) app.data = {};
					const openFiles = app.data.openFiles;
					if (!Array.isArray(openFiles)) {
						app.data.openFiles = [action.path];
					} else if (!openFiles.includes(action.path)) {
						app.data.openFiles = [...openFiles, action.path];
					}
					openApp(ds, app.id, app.name, app.icon);
				}
			} else if (action.type.endsWith("CloseFile") && hasAppAndPath(action)) {
				const app = ds.System.Manager.Applications.apps[action.app.id];
				if (app?.data) {
					const openFiles = app.data.openFiles;
					if (Array.isArray(openFiles)) {
						app.data.openFiles = openFiles.filter(
							(p: unknown) => p !== action.path,
						);
					}
				}
			}
			break;
		}
	}

	return ds;
};

export const classicyShortcutEventHandler = (
	ds: ClassicyStore,
	action: ActionMessage,
): ClassicyStore => {
	const kb = ds.System.Manager.Keyboard;
	switch (action.type) {
		case "ClassicyShortcutRegister": {
			if (action.scope === "app" && typeof action.appId === "string") {
				kb.app[action.appId] = Array.isArray(action.chords)
					? [...new Set(action.chords as string[])]
					: [];
			} else if (action.scope === "system") {
				kb.system = Array.isArray(action.chords)
					? [...new Set(action.chords as string[])]
					: [];
			} else if (
				action.scope === "global" &&
				typeof action.chord === "string" &&
				action.chord !== "" &&
				typeof action.event === "string"
			) {
				if (kb.global[action.chord]) {
					classicyLog(
						"warn",
						"ClassicyShortcut",
						"global chord already registered; ignoring",
						{ chord: action.chord, by: action.appId },
					);
				} else {
					kb.global[action.chord] = {
						appId: String(action.appId ?? ""),
						event: action.event,
						eventData: action.eventData as Record<string, unknown> | undefined,
					};
				}
			}
			break;
		}
		case "ClassicyShortcutUnregister": {
			if (action.scope === "app" && typeof action.appId === "string") {
				delete kb.app[action.appId];
			} else if (
				action.scope === "global" &&
				typeof action.chord === "string"
			) {
				const owner = kb.global[action.chord];
				if (owner && owner.appId === action.appId) {
					delete kb.global[action.chord];
				}
			}
			break;
		}
	}
	return ds;
};

export const classicyDesktopStateEventReducer = (
	ds: ClassicyStore,
	action: ActionMessage,
	trust: ClassicyActionTrust = "trusted",
) => {
	if ("debug" in action && process.env.NODE_ENV !== "production") {
		console.group("Desktop Event");
		console.log("Action: ", action);
		console.log("Start State: ", ds);
	}

	if ("type" in action) {
		// Kernel trust guard: an untrusted action (e.g. dispatched on behalf of
		// a HyperCard stack script) is rejected before ANY handler — built-in or
		// plugin — sees it, if its type falls under a guarded route. This must
		// run before the prefix routing below, since plugin handlers are also a
		// route into the reducer and are not otherwise distinguishable here.
		if (!isActionTrustPermitted(trust, action.type)) {
			classicyLog(
				"warn",
				"ClassicyDesktopStateEventReducer",
				"Rejected untrusted action",
				{ type: action.type },
			);
		} else if (action.type.startsWith("ClassicyWindow")) {
			ds = classicyWindowEventHandler(ds, action);
		} else if (action.type.startsWith("ClassicyDesktopIcon")) {
			ds = classicyDesktopIconEventHandler(ds, action);
		} else if (action.type.startsWith("ClassicyDesktop")) {
			ds = classicyDesktopEventHandler(ds, action);
		} else if (action.type.startsWith("ClassicyBootParadeIcon")) {
			ds = classicyBootEventHandler(ds, action);
		} else if (action.type.startsWith("ClassicyManagerDateTime")) {
			ds = classicyDateTimeManagerEventHandler(ds, action);
		} else if (action.type.startsWith("ClassicyShortcut")) {
			ds = classicyShortcutEventHandler(ds, action);
		} else {
			const plugin = pluginEventHandlers.find(({ prefix }) =>
				action.type.startsWith(prefix),
			);
			if (plugin) {
				ds = plugin.handler(ds, action);
			} else if (action.type.startsWith("ClassicyApp")) {
				ds = classicyAppEventHandler(ds, action);
			} else {
				classicyLog(
					"warn",
					"ClassicyDesktopStateEventReducer",
					"Unhandled action type",
					{ type: action.type },
				);
			}
		}
	}

	if ("debug" in action && process.env.NODE_ENV !== "production") {
		console.log("End State: ", ds);
		console.groupEnd();
	}

	return ds;
};

/**
 * Deep-merge `overrides` onto a structural clone of `base`.
 * Plain objects merge recursively; arrays and primitives from `overrides`
 * replace the base value wholesale; `undefined` override values are skipped;
 * `base` is never mutated.
 */
export function mergeClassicyState(
	base: ClassicyStore,
	overrides: DeepPartial<ClassicyStore>,
): ClassicyStore {
	return deepMergeReplacingArrays(base, overrides);
}

export const DefaultAppManagerState: ClassicyStore = {
	System: {
		Manager: {
			DateAndTime: {
				show: true,
				dateTime: new Date().toISOString(),
				timeZoneOffset: (new Date().getTimezoneOffset() / -60).toString(),
				militaryTime: false,
				displaySeconds: true,
				displayPeriod: true,
				displayDay: true,
				displayLongDay: false,
				flashSeparators: true,
				paused: false,
				minDateTime: null,
				maxDateTime: null,
				boundaryLocked: false,
				dateTimeLocked: false,
				syncTimeOnly: false,
			},
			Sound: {
				volume: 100,
				labels: {},
				disabled: [],
			},
			Desktop: {
				selectedIcons: [],
				icons: [],
				systemMenu: [
					{
						id: "about",
						title: "About This Computer",
						keyboardShortcut: "⌃S",
						event: "ClassicyAppFinderAboutThisComputerOpen",
						eventData: {},
					},
					{ id: "spacer" },
				],
				appMenu: [],
				selectBox: {
					size: [0, 0],
					start: [0, 0],
					active: false,
				},
				disableBalloonHelp: false,
				doubleClickTitleToCollapse: true,
				errorDialog: null,
				fsVersion: 0,
			},
			Applications: {
				focusedAppId: "Finder.app",
				apps: {
					"Finder.app": {
						id: "Finder.app",
						name: "Finder",
						icon: macosIcon,
						windows: [],
						open: true,
						focused: true,
						noDesktopIcon: true,
						data: {},
						handlesFileTypes: Object.values(ClassicyFileSystemEntryFileType),
					},
				},
				fileTypeHandlers: Object.fromEntries(
					Object.values(ClassicyFileSystemEntryFileType).map((type) => [
						type,
						"Finder.app",
					]),
				) as Record<ClassicyFileSystemEntryFileType, string>,
			},
			Appearance: {
				availableThemes: getAllThemes(),
				// Via getTheme, not themesData: the raw JSON names its wallpaper
				// rather than carrying a loadable URL.
				activeTheme: getTheme("default"),
				alertSound: DEFAULT_ALERT_SOUND,
			},
			Boot: {
				paradeIcons: [],
			},
			Keyboard: {
				app: {},
				system: [],
				global: {},
			},
		},
	},
};
